import _ from 'lodash';
import { sqCapsuleGroupStore, sqInvestigateStore, sqTrendCapsuleStore } from '@/core/core.stores';
import { splitUniqueId } from '@/trendData/trendCapsule.utilities';
import { PREVIEW_ID } from '@/trendData/trendData.constants';
import type { CapsuleGroupCapsule } from '@/tools/manualCondition/manualCondition.constants';

/**
 * Reconciles the capsule group with the current trend capsule selection. Called directly from every trend action
 * that dispatches a Flux action affecting `sqTrendCapsuleStore`'s `selectedCapsules`/`editingId` (selection,
 * replacement, addition, removal, and edit-mode changes), so the dependency on the capsule group is explicit at
 * each call site instead of hidden behind a Flux listener. Lives outside capsuleGroup.actions.ts so that those
 * trend actions (which live in trendData/) can import it without a circular dependency, since
 * capsuleGroup.actions.ts itself imports from trendData/trendCapsule.actions.ts.
 */
export function takeCapsulesFromSelection() {
  if (!sqInvestigateStore.isCapsulePickingMode) {
    return;
  }

  const selections = sqTrendCapsuleStore.selectedCapsules;

  const [otherCapsules, selectedCapsules] = _.partition(
    sqCapsuleGroupStore.getState().capsules,
    (capsule) => capsule.id === undefined || capsule.id === null,
  );

  const newSelectedCapsules = _.chain(selections)
    // Preview capsules should not be copied even if they are selected
    .reject((s) => splitUniqueId(s.id).conditionId === (sqTrendCapsuleStore.editingId || PREVIEW_ID))
    .map((selection): CapsuleGroupCapsule => {
      const split = splitUniqueId(selection.id);
      return (
        selectedCapsules.find((c) => c.id === selection.id) ?? {
          id: selection.id,
          startTime: selection.startTime!,
          endTime: selection.endTime!,
          propertiesSource: {
            ...selection,
            startTime: selection.startTime!,
            endTime: selection.endTime!,
            id: selection.id,
            capsuleId: split.capsuleId,
            conditionId: split.conditionId,
            isUncertain: selection.isUncertain ?? false,
          },
        }
      );
    })
    .value();

  // Uses the store action directly (not manualCondition.actions.ts's setCapsules wrapper) so this internal
  // recompute does not also trigger the manual condition preview as a side effect.
  sqCapsuleGroupStore.actions.setCapsules(newSelectedCapsules.concat(otherCapsules));
}
